import styled, { CSSObject } from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import { useState, useEffect, useCallback, useRef } from 'react'
import throttle from 'lodash/throttle'
import {
  DEFAULT_NAVBAR_HEIGHT,
  EXPANDED_NAVBAR_HEIGHT,
  navBarHeightTransition
} from '../NavBar/commonStyles'
import { isIOS, isMacOs, isSafari } from 'react-device-detect'
import {
  ShopCardType,
  SHOP_INFO,
  iosGpuRenderFixProperties
} from './commonStyles'

interface StickyCardProps {
  id?: string
  header?: React.ReactNode
  content?: React.ReactNode
  children?: React.ReactNode
  hasHeaderOffset?: boolean
  type: ShopCardType
  sidebar?: React.ReactNode
  banner?: React.ReactNode
  fulfillment?: React.ReactNode
}

export const StickyCard = ({
  id,
  header,
  content,
  children,
  hasHeaderOffset,
  type,
  sidebar,
  banner,
  fulfillment
}: StickyCardProps) => {
  const [hasShadow, setHasShadow] = useState(false)
  const [navBarHeight, setNavBarHeight] = useState<number>(
    DEFAULT_NAVBAR_HEIGHT
  )
  const headerRef = useRef<null | HTMLDivElement>(null)

  const handleScroll = useCallback(() => {
    if (!headerRef?.current) {
      return
    }

    const offsetY = 48
    const marginTop = headerRef.current.getBoundingClientRect().top - offsetY

    if (!hasShadow && marginTop <= 0) {
      setHasShadow(true)
      return
    }

    if (hasShadow && marginTop > 0) {
      setHasShadow(false)
    }
  }, [hasShadow])

  useEffect(() => {
    const onScroll = throttle(handleScroll, 300)

    document.addEventListener('scroll', onScroll)

    return () => {
      document.removeEventListener('scroll', onScroll)
    }
  }, [handleScroll])

  const handleNavBarHeightOffset = useCallback(() => {
    if (navBarHeight === DEFAULT_NAVBAR_HEIGHT)
      return setNavBarHeight(EXPANDED_NAVBAR_HEIGHT)
    if (navBarHeight === EXPANDED_NAVBAR_HEIGHT)
      return setNavBarHeight(DEFAULT_NAVBAR_HEIGHT)
  }, [navBarHeight])

  useEffect(() => {
    const navBarSearchIcon = document.getElementById('nav-bar-search-icon')
    if (navBarSearchIcon)
      navBarSearchIcon.addEventListener('click', handleNavBarHeightOffset)

    return () => {
      if (navBarSearchIcon)
        navBarSearchIcon.removeEventListener('click', handleNavBarHeightOffset)
    }
  }, [handleNavBarHeightOffset])

  return (
    <Root id={id} hasSidebar={!!sidebar}>
      <MainColumn>
        <StickyHeader
          ref={headerRef}
          hasHeaderOffset={hasHeaderOffset}
          navBarHeight={navBarHeight}
        >
          <HeaderWrapper
            hasHeaderOffset={hasHeaderOffset}
            hasShadow={hasShadow}
          >
            {header}
          </HeaderWrapper>
        </StickyHeader>
        {banner && banner}
        <ContentLayout type={type} hasBanner={!!banner}>
          <ContentContainer cardType={type}>
            {content || children}
          </ContentContainer>
        </ContentLayout>
      </MainColumn>
      {sidebar &&
        (fulfillment ? (
          <SidebarContainer>
            {fulfillment}
            <SidebarColumn hasFulfillment={true}>{sidebar}</SidebarColumn>
          </SidebarContainer>
        ) : (
          <SidebarColumn hasFulfillment={false}>{sidebar}</SidebarColumn>
        ))}
    </Root>
  )
}

const ContentLayout = styled.div<
  StyledHTMLElement & { hasBanner: boolean; type: ShopCardType }
>(({ hasBanner, type }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '16px',
  boxShadow: '0px 5px 32px rgba(0, 0, 0, 0.07)',
  marginTop: type === 'SHOP_INFO' ? '0' : hasBanner ? '0' : '16px'
}))

const Root = styled.div<StyledHTMLElement & { hasSidebar: boolean }>(
  ({ theme, hasSidebar }) => ({
    position: 'relative',
    display: hasSidebar ? 'grid' : 'block',
    gridTemplateColumns: hasSidebar ? '1fr 430px' : undefined,
    gap: hasSidebar ? '24px' : undefined,
    marginTop: '16px',

    [theme.mediaQueries.viewport7]: {
      marginTop: 0
    }
  })
)

const MainColumn = styled.div({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0
})

const SidebarContainer = styled.div<StyledHTMLElement>(
  ({ theme }): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    position: 'sticky',
    top: DEFAULT_NAVBAR_HEIGHT, // Sidebar only active on desktop, so used default navbar height (not dynamic navBarHeight useState)
    alignSelf: 'start'
  })
)

const SidebarColumn = styled.div<
  StyledHTMLElement & { hasFulfillment: boolean }
>(({ hasFulfillment }) => {
  return {
    marginTop: !hasFulfillment ? '16px' : '0',
    width: '100%'
  }
})

const ContentContainer = styled.div<
  StyledHTMLElement & { cardType: ShopCardType; hasHeaderOffset?: boolean }
>(({ theme, hasHeaderOffset = false, cardType }) => {
  // Shop info card at bottom of shop page
  if (cardType === SHOP_INFO) {
    return {
      backgroundColor: 'white',
      padding: hasHeaderOffset ? '16px' : '0 16px 16px 16px',
      [theme.mediaQueries.viewport7]: {
        padding: hasHeaderOffset ? '24px' : '0 24px 24px 24px',
        borderRadius: hasHeaderOffset ? '12px' : '0 0 12px 12px',
        overflow: 'hidden'
      }
    }
  }
  // Product list card on shop page
  return {
    overflow: 'hidden',
    display: 'grid',
    gap: '8px',
    [theme.mediaQueries.viewport7]: {
      margin: '0',
      overflow: 'hidden'
    },
    [theme.mediaQueries.viewport9]: {
      borderRadius: theme.borderRadius,
      overflow: 'hidden',
      padding: '0',
      backgroundColor: 'white',
      gap: '16px'
    }
  }
})

const StickyHeader = styled.div<
  StyledHTMLElement & { hasHeaderOffset?: boolean; navBarHeight: number }
>(({ theme, hasHeaderOffset = false, navBarHeight }) => {
  // react-device-detect and the Chrome Dev tools emulation of an iPhone results in isSafari: true
  const isAppleSafari = (isMacOs || isIOS) && isSafari
  // On mobile, the fulfillment section is sticky, so top value takes into account navbar & fulfillment section height
  const stickyFulfillmentHeight =
    document.getElementById('fulfillment-container')?.offsetHeight || 60
  const top = navBarHeight + stickyFulfillmentHeight
  return {
    position: (isAppleSafari ? `-webkit-sticky !important` : 'sticky') as any,
    top: `${top}px`,
    left: 0,
    zIndex: 10,
    transition: navBarHeightTransition,

    ...iosGpuRenderFixProperties,

    backgroundColor: '#f5f5f5',

    [theme.mediaQueries.viewport7]: {
      top: `${navBarHeight}px`,
      paddingTop: '16px',
      borderRadius: '0 0 12px 12px'
    }
  }
})

const HeaderWrapper = styled.div<
  StyledHTMLElement & { hasShadow: boolean; hasHeaderOffset?: boolean }
>(
  ({ theme, hasShadow, hasHeaderOffset = false }): CSSObject => ({
    backgroundColor: 'white',
    minHeight: '62px',
    padding: '16px',
    boxShadow: hasShadow ? '0px 5px 32px rgba(0, 0, 0, 0.07)' : 'none',

    [theme.mediaQueries.viewport7]: {
      boxShadow:
        hasHeaderOffset || hasShadow
          ? '0px 5px 32px rgba(0, 0, 0, 0.07)'
          : undefined,
      padding: '12px 24px',
      borderRadius: hasHeaderOffset
        ? theme.borderRadius
        : `${theme.borderRadius} ${theme.borderRadius} 0 0`
    }
  })
)
